import { useState, useEffect } from 'react';
import { Link, UserPlus, UserMinus, Share2 } from 'lucide-react';
import { HelpTip } from './HelpTip';
import { TIPS } from '../help/content';
import {
  getSharedReportDoc,
  markReportShared,
  markReportUnshared,
  grantAccess,
  revokeAccess,
  changeAccess,
  lookupUserByEmail,
} from '../utils/sharingStorage';

import { getNextTierThatAllowsFeature, getNextTier} from '../hooks/useTier';

const LABEL_STYLE = {
  fontSize: 12,
  fontWeight: 700,
  color: "#6B7280",
  letterSpacing: 0.8,
  textTransform: "uppercase",
  marginBottom: 12,
};

function AccessToggle({ value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1.5px solid #E5E7EB", flexShrink: 0 }}>
      {['view', 'edit'].map((level) => (
        <button
          key={level}
          onClick={() => !disabled && onChange(level)}
          disabled={disabled}
          style={{
            padding: "5px 10px",
            border: "none",
            fontSize: 11,
            fontWeight: 700,
            cursor: disabled ? "default" : "pointer",
            background: value === level ? (level === 'edit' ? "#0A7C8A" : "#6B7280") : "#fff",
            color: value === level ? "#fff" : "#9CA3AF",
            transition: "background 0.15s, color 0.15s",
          }}
        >
          {level === 'view' ? 'View' : 'Edit'}
        </button>
      ))}
    </div>
  );
}

function ShareModal({ uid, userEmail, filename, onClose, onSharedChange, tier, tierLimits, onUpgrade , tierConfig}) {
  const [sharedDoc, setSharedDoc] = useState(undefined); // undefined = loading
  const [emailInput, setEmailInput] = useState('');
  const [newAccessLevel, setNewAccessLevel] = useState('edit');
  const [addingEmail, setAddingEmail] = useState(false);
  const [addError, setAddError] = useState(null);
  const [revoking, setRevoking] = useState(new Set());
  const [changingAccess, setChangingAccess] = useState(new Set());
  const [toggling, setToggling] = useState(false);

  // Tier gates
  const canShare      = tierLimits ? (tierLimits.canShare ?? true) : true;
  const canShareEdit  = tierLimits ? (tierLimits.canShareEdit ?? true) : true;
  const maxCollabs    = tierLimits?.maxCollaborators ?? Infinity;

  useEffect(() => {
    getSharedReportDoc(uid, filename).then(setSharedDoc);
  }, [uid, filename]);

  const handleStartSharing = async () => {
    setToggling(true);
    try {
      await markReportShared(uid, filename);
      setSharedDoc({ filename, grantees: [] });
      onSharedChange?.();
    } catch (err) {
      console.error('Failed to mark shared:', err);
    } finally {
      setToggling(false);
    }
  };

  const handleStopSharing = async () => {
    if (!window.confirm(`Stop sharing "${filename}"? All collaborators will lose access immediately.`)) return;
    setToggling(true);
    try {
      await markReportUnshared(uid, filename);
      setSharedDoc(null);
      onSharedChange?.();
      onClose();
    } catch (err) {
      console.error('Failed to unshare:', err);
    } finally {
      setToggling(false);
    }
  };

  const handleAddEmail = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    if (email === userEmail?.toLowerCase()) {
      setAddError("That's your own email address.");
      return;
    }
    const already = (sharedDoc?.grantees ?? []).some(
      (g) => g.email.toLowerCase() === email
    );
    if (already) {
      setAddError('This person already has access.');
      return;
    }

    setAddingEmail(true);
    setAddError(null);
    try {
      const grantee = await lookupUserByEmail(email);
      if (!grantee) {
        setAddError('No account found for that email. They must sign in to Rivnus first.');
        return;
      }
      const effectiveAccess = canShareEdit ? newAccessLevel : 'view';
      await grantAccess(uid, userEmail, filename, grantee.uid, grantee.email, effectiveAccess);
      setSharedDoc((prev) => ({
        ...prev,
        grantees: [...(prev?.grantees ?? []), { uid: grantee.uid, email: grantee.email, access: effectiveAccess }],
      }));
      setEmailInput('');
      onSharedChange?.();
    } catch (err) {
      console.error('grantAccess failed:', err);
      if (err.code === 'permission-denied') {
        setAddError('Permission denied — Firestore security rules need to be deployed. See console.');
      } else {
        setAddError('Failed to add collaborator. Please try again.');
      }
    } finally {
      setAddingEmail(false);
    }
  };

  const handleRevoke = async (grantee) => {
    setRevoking((prev) => new Set(prev).add(grantee.uid));
    try {
      await revokeAccess(uid, filename, grantee.uid);
      setSharedDoc((prev) => ({
        ...prev,
        grantees: (prev?.grantees ?? []).filter((g) => g.uid !== grantee.uid),
      }));
      onSharedChange?.();
    } catch (err) {
      console.error('revokeAccess failed:', err);
    } finally {
      setRevoking((prev) => { const next = new Set(prev); next.delete(grantee.uid); return next; });
    }
  };

  const handleChangeAccess = async (grantee, newAccess) => {
    if (grantee.access === newAccess) return;
    setChangingAccess((prev) => new Set(prev).add(grantee.uid));
    try {
      await changeAccess(uid, filename, grantee.uid, newAccess);
      setSharedDoc((prev) => ({
        ...prev,
        grantees: (prev?.grantees ?? []).map((g) =>
          g.uid === grantee.uid ? { ...g, access: newAccess } : g
        ),
      }));
      onSharedChange?.();
    } catch (err) {
      console.error('changeAccess failed:', err);
    } finally {
      setChangingAccess((prev) => { const next = new Set(prev); next.delete(grantee.uid); return next; });
    }
  };

  const grantees = sharedDoc?.grantees ?? [];

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 1100, backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff", borderRadius: "20px 20px 0 0", padding: "20px",
          width: "100%", maxWidth: 390, maxHeight: "80vh", overflowY: "auto",
          animation: "slideUp 0.3s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Share2 size={18} color="#0A7C8A" />
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#111" }}>Share Report</h2>
            <HelpTip content={TIPS.sharing} placement="top" />
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#888", padding: 0, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <strong style={{ color: "#111" }}>{filename}</strong>
        </div>

        {sharedDoc === undefined && (
          <div style={{ textAlign: "center", padding: "32px 0", color: "#9CA3AF", fontSize: 14 }}>Loading…</div>
        )}

        {/* Tier gate: sharing not available on this plan */}
        {!canShare && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 8 }}>Sharing requires an upgrade</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 20 }}>Report sharing is not available on your current plan.</div>
            <button
              onClick={() => { onClose(); onUpgrade?.({ allowed: false, featureName: "Report sharing", message: "Report sharing is not available on your current plan.", 
                upgradeTo: getNextTierThatAllowsFeature(tier, "canShare", tierConfig), 
                upgradeMessage: "Upgrade to share reports with collaborators." }); }}
              style={{ padding: "12px 24px", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
            >
              View upgrade options
            </button>
          </div>
        )}

        {canShare && sharedDoc === null && (
          <div>
            <div style={{
              background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 12,
              padding: "20px", textAlign: "center", marginBottom: 20,
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#111", marginBottom: 4 }}>This report is private</div>
              <div style={{ fontSize: 13, color: "#6B7280" }}>Enable sharing to invite collaborators.</div>
            </div>
            <button
              onClick={handleStartSharing}
              disabled={toggling}
              style={{
                width: "100%", padding: "14px", borderRadius: 12, border: "none",
                background: "linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)", color: "#fff",
                fontSize: 14, fontWeight: 700, cursor: toggling ? "not-allowed" : "pointer",
                opacity: toggling ? 0.7 : 1, display: "flex", alignItems: "center",
                justifyContent: "center", gap: 8,
              }}
            >
              <Link size={16} />
              {toggling ? "Enabling…" : "Start Sharing"}
            </button>
          </div>
        )}

        {canShare && sharedDoc && sharedDoc !== undefined && (
          <div>
            {/* Shared badge */}
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#ECFDF5", border: "1px solid #6EE7B7", borderRadius: 20,
              padding: "4px 12px", marginBottom: 24, fontSize: 12, fontWeight: 700, color: "#059669",
            }}>
              <Link size={12} /> Sharing enabled
            </div>

            {/* Add collaborator */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ ...LABEL_STYLE, display: "flex", alignItems: "center", gap: 4 }}>
              Add collaborator
              <HelpTip content="The collaborator must already have a MySpend account. Enter their sign-in email address." placement="top" />
            </div>
              {grantees.length >= maxCollabs ? (
                <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#92400E" }}>
                  Collaborator limit reached ({grantees.length}/{maxCollabs}).{" "}
                  <button onClick={() => { onClose(); onUpgrade?.({ allowed: false, featureName: "Collaborators", message: `Your plan allows up to ${maxCollabs} collaborator${maxCollabs !== 1 ? 's' : ''}.`, 
                    upgradeTo: getNextTier(tier), upgradeMessage: "Upgrade to add more collaborators." }); }} style={{ background: "none", border: "none", padding: 0, color: "#0A7C8A", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>Upgrade</button>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => { setEmailInput(e.target.value); setAddError(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                      placeholder="colleague@example.com"
                      style={{
                        flex: 1, padding: "10px 14px", borderRadius: 10,
                        border: addError ? "1.5px solid #EF4444" : "1.5px solid #E5E7EB",
                        fontSize: 14, outline: "none", color: "#111",
                      }}
                    />
                    <button
                      onClick={handleAddEmail}
                      disabled={addingEmail || !emailInput.trim()}
                      style={{
                        padding: "10px 16px", borderRadius: 10, border: "none",
                        background: "linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)", color: "#fff",
                        fontSize: 13, fontWeight: 700,
                        cursor: addingEmail || !emailInput.trim() ? "not-allowed" : "pointer",
                        opacity: addingEmail || !emailInput.trim() ? 0.6 : 1,
                        display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                      }}
                    >
                      <UserPlus size={14} />
                      {addingEmail ? "Adding…" : "Add"}
                    </button>
                  </div>

                  {/* Access level picker — hide edit toggle if canShareEdit is false */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#6B7280", fontWeight: 600 }}>
                      Access: <HelpTip content={TIPS.shareAccess} placement="top" />
                    </span>
                    {canShareEdit ? (
                      <AccessToggle value={newAccessLevel} onChange={setNewAccessLevel} />
                    ) : (
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", background: "#F3F4F6", padding: "5px 10px", borderRadius: 6 }}>View only</div>
                    )}
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                      {canShareEdit
                        ? (newAccessLevel === 'edit' ? 'Can view & edit' : 'Can view only')
                        : 'Upgrade to Pro for edit access'
                      }
                    </span>
                  </div>
                </>
              )}

              {addError && (
                <div style={{ fontSize: 12, color: "#EF4444", marginTop: 6 }}>{addError}</div>
              )}
            </div>

            {/* Grantee list */}
            <div style={{ marginBottom: 24 }}>
              <div style={LABEL_STYLE}>
                {grantees.length === 0 ? "No collaborators yet" : `Collaborators (${grantees.length})`}
              </div>
              {grantees.length === 0 ? (
                <div style={{ fontSize: 13, color: "#9CA3AF" }}>Add someone by email above.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {grantees.map((g) => {
                    const isRevoking = revoking.has(g.uid);
                    const isChanging = changingAccess.has(g.uid);
                    const access = g.access ?? 'edit';
                    return (
                      <div
                        key={g.uid}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          background: "#F9FAFB", border: "1px solid #E5E7EB",
                          borderRadius: 10, padding: "10px 14px",
                          opacity: isRevoking ? 0.5 : 1,
                        }}
                      >
                        <div style={{
                          width: 32, height: 32, borderRadius: 16,
                          background: "linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0,
                        }}>
                          {g.email[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: "#111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {g.email}
                        </div>
                        <AccessToggle
                          value={access}
                          onChange={(newAccess) => handleChangeAccess(g, newAccess)}
                          disabled={isRevoking || isChanging || !canShareEdit}
                        />
                        <button
                          onClick={() => handleRevoke(g)}
                          disabled={isRevoking}
                          title="Remove access"
                          style={{
                            background: "none", border: "none",
                            color: isRevoking ? "#9CA3AF" : "#EF4444",
                            cursor: isRevoking ? "not-allowed" : "pointer",
                            padding: 4, display: "flex", alignItems: "center",
                          }}
                        >
                          <UserMinus size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Stop sharing */}
            <div style={{ paddingTop: 16, borderTop: "1px solid #E5E7EB" }}>
              <button
                onClick={handleStopSharing}
                disabled={toggling}
                style={{
                  width: "100%", padding: "12px", borderRadius: 10,
                  border: "1.5px solid #FCA5A5", background: "#FFF5F5",
                  color: "#EF4444", fontSize: 13, fontWeight: 700,
                  cursor: toggling ? "not-allowed" : "pointer", opacity: toggling ? 0.6 : 1,
                }}
              >
                {toggling ? "Stopping…" : "Stop Sharing & Revoke All Access"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ShareModal;
